import { useState, useEffect, useRef } from 'react'
import Quote from '@/components/ui/quote'

import ArrowRight from '@/icons/bwi/angle-right.svg'
import ArrowLeft from '@/icons/bwi/angle-left.svg'

import type { Entry, EntryFieldTypes } from 'contentful'
import { slugify, cx } from '@/utils/strings'
import RichText from '../ui/rich-text'

import SliderPresentation from '@/components/ui/slider/slider-presentation'
import {
  TypeComponentQuoteSkeleton,
  TypeComponentTableSkeleton,
  TypeResourceImageSkeleton,
  TypeSectionSetOfCardsSkeleton,
} from '@/types/ctf'

// Define a union type for the possible slide types
type SlideType =
  | TypeComponentQuoteSkeleton
  | TypeComponentTableSkeleton
  | TypeResourceImageSkeleton
  | TypeSectionSetOfCardsSkeleton

interface SliderProps {
  internalName: string
  headline?: string
  eyebrow?: string
  body?: EntryFieldTypes.RichText
  slides: Entry<SlideType>[]
  sliderStyle?: 'Presentation' | 'Default'
  pdfPath?: string
  marquee?: boolean
}

export default function Slider({
  internalName,
  body,
  slides,
  headline,
  eyebrow,
  sliderStyle,
  pdfPath,
  marquee = false,
}: SliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const [slideWidth, setSlideWidth] = useState(0)
  const [isLastSlideVisible, setIsLastSlideVisible] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  // Animation state
  const [isPaused, setIsPaused] = useState(false)
  const animationRef = useRef<number | null>(null)
  const [translateX, setTranslateX] = useState(0)

  // Variables to track touch positions
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  // Calculate actual slide widths for precise movement
  useEffect(() => {
    // Initialize refs array
    slideRefs.current = slideRefs.current.slice(0, slides.length)

    const calculateSlideWidths = () => {
      if (slideRefs.current[0]?.offsetWidth) {
        // Get the width of a slide including its padding
        const slideElement = slideRefs.current[0]
        const slideElementWidth = slideElement.offsetWidth
        const sliderWidth = sliderRef.current ? sliderRef.current.offsetWidth : 0

        // Calculate the exact percentage this slide takes in the slider
        const exactSlideWidthPercentage = (slideElementWidth / sliderWidth) * 100
        setSlideWidth(exactSlideWidthPercentage)

        // Calculate total width of all slides
        const trackWidth = slideElementWidth * slides.length

        checkLastSlideVisibility(currentIndex, sliderWidth, slideElementWidth, slides.length)

        if (trackWidth - 40 <= sliderWidth && currentIndex > 0) {
          // if the whole track fits on the screen, reset navigation
          setCurrentIndex(0)
        }
      }
    }

    calculateSlideWidths()

    // Recalculate on window resize
    window.addEventListener('resize', calculateSlideWidths)
    return () => window.removeEventListener('resize', calculateSlideWidths)
  }, [slides.length, currentIndex])

  const checkLastSlideVisibility = (index: number, sliderWidth: number, slideWidth: number, totalItems: number) => {
    const test = slideWidth - 4
    const visibleSlidesCount = sliderWidth / test

    const isLastVisible = index + visibleSlidesCount >= totalItems
    setIsLastSlideVisible(isLastVisible)
  }

  useEffect(() => {
    if (slideRefs.current[0]?.offsetWidth) {
      const slideWidth = slideRefs.current[0].offsetWidth
      const sliderWidth = sliderRef.current ? sliderRef.current.offsetWidth : 0
      checkLastSlideVisibility(currentIndex, sliderWidth, slideWidth, slides.length)
    }
  }, [currentIndex, slides.length])

  // Marquee animation functionality
  useEffect(() => {
    if (!marquee || isPaused) {
      // If marquee is disabled or paused, cancel any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      return
    }

    let lastTime: number | null = null

    // Calculate a responsive speed based on viewport width
    // This makes the marquee appear consistent across different screen sizes
    const baseSpeed = 0.08 // Base speed factor
    const viewportWidth = window.innerWidth
    const speed = baseSpeed * (viewportWidth / 1000) // Scale with viewport

    const animate = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp

      // Calculate delta time for smooth animation
      const deltaTime = timestamp - lastTime
      lastTime = timestamp

      // Update position - move to the left (negative) with responsive speed
      let newPosition = translateX - speed * deltaTime

      // Get the width of a single slide
      const singleSlideWidth = slideRefs.current[0]?.offsetWidth || 0
      const totalWidth = singleSlideWidth * slides.length

      // When we've moved past the total width of all original slides, reset position
      // Add a small buffer to ensure seamless looping
      if (Math.abs(newPosition) >= totalWidth) {
        newPosition = 0
      }

      setTranslateX(newPosition)

      // Continue the animation loop
      animationRef.current = requestAnimationFrame(animate)
    }

    // Start the animation
    animationRef.current = requestAnimationFrame(animate)

    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [marquee, isPaused, translateX, slides.length])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prevSlide()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        nextSlide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentIndex])

  // Swipe controls
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (marquee) setIsPaused(true)
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (marquee) setIsPaused(false)

    if (touchStartX.current === null || touchEndX.current === null) {
      touchStartX.current = null
      touchEndX.current = null
      return
    }

    const deltaX = touchStartX.current - touchEndX.current

    if (deltaX > 30) {
      nextSlide()
    } else if (deltaX < -30) {
      prevSlide()
    }

    // Reset touch positions
    touchStartX.current = null
    touchEndX.current = null
  }

  const nextSlide = () => {
    if (!isLastSlideVisible) {
      requestAnimationFrame(() => {
        setCurrentIndex((prev) => prev + 1)
      })
    }
  }

  const prevSlide = () => {
    if (currentIndex > 0) {
      requestAnimationFrame(() => {
        setCurrentIndex((prev) => prev - 1)
      })
    }
  }

  // pauseMarquee function removed

  const controlButtonStyles = 'w-[20px] sm:w-[34px]'
  const disabledButtonStyles = 'text-muted cursor-not-allowed'
  const enabledButtonStyles = 'text-primaryBlue hover:text-indigoBlue dark:hover:text-secondary-50'
  const slideCommon = 'h-full flex-shrink-0 px-2 w-[calc(100vw-100px)] sm:w-[410px]'

  const renderSliderBtn = (direction: string) => {
    // Don't render navigation buttons if marquee is true
    if (marquee) return null

    return (
      <button
        onClick={() => {
          direction === 'left' ? prevSlide() : nextSlide()
        }}
        disabled={direction === 'left' ? currentIndex === 0 : isLastSlideVisible}
        className={cx(
          'absolute top-0 h-full',
          direction === 'left' ? '-left-3 lg:-left-4' : '-right-3 lg:-right-4',
          controlButtonStyles,
          direction === 'left' && currentIndex === 0 && disabledButtonStyles,
          direction === 'left' && currentIndex > 0 && enabledButtonStyles,
          direction === 'right' && isLastSlideVisible && disabledButtonStyles,
          direction === 'right' && !isLastSlideVisible && enabledButtonStyles
        )}
        aria-label={direction === 'left' ? 'Previous slide' : 'Next slide'}
      >
        {direction === 'left' ? <ArrowLeft /> : <ArrowRight />}
      </button>
    )
  }

  return sliderStyle === 'Presentation' ? (
    <section id={slugify(internalName)}>
      {headline && <h3 className="text-center">{headline}</h3>}
      <SliderPresentation
        customCss="md:[--bw-slider-nav-button-distance:2.44rem]"
        pdfPath={pdfPath}
        slides={slides}
        marquee={marquee}
      />
    </section>
  ) : (
    <section
      className="mx-auto max-w-8xl px-5 lg:px-9 focus:outline-none"
      aria-label="slider"
      aria-roledescription="carousel"
      aria-live="off"
    >
      <section className="slider-content">
        {(headline || eyebrow || body) && (
          <div className={cx('relative', !headline && !eyebrow && !body && 'pb-12')}>
            <div className="px-2 mb-8 text-center">
              {eyebrow && <p className="text-eyebrow uppercase">{eyebrow}</p>}
              {headline && <h3>{headline}</h3>}
              {body && <RichText body={body} />}
            </div>
          </div>
        )}
        <div
          className={cx(!marquee && slides.length >= 4 && 'pl-3 pr-5 sm:pl-5 sm:pr-7', 'relative')}
          aria-label="Slider controls"
        >
          {slides.length >= 4 && (
            <>
              {renderSliderBtn('left')}
              {renderSliderBtn('right')}
            </>
          )}

          {slides.length >= 4 ? (
            <div
              className={cx(
                'relative overflow-hidden rounded-sm',
                (!isLastSlideVisible || marquee) &&
                  'after:absolute after:right-0 after:top-0 after:h-full after:w-2 after:bg-gradient-to-l after:from-secondary-100 after:to-transparent',
                marquee &&
                  'before:z-10 before:absolute before:left-0 before:top-0 before:h-full before:w-2 before:bg-gradient-to-r before:from-secondary-100 before:to-transparent'
              )}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseEnter={() => marquee && setIsPaused(true)}
              onMouseLeave={() => marquee && setIsPaused(false)}
              aria-label="Slider track"
            >
              <div className="grid grid-cols-1">
                <div
                  ref={sliderRef}
                  className={cx(
                    'flex flex-row will-change-transform',
                    !marquee && 'transition-transform duration-500 ease-out'
                  )}
                  style={{
                    transform: marquee
                      ? `translate3d(${translateX}px, 0, 0)`
                      : `translate3d(-${currentIndex * slideWidth}%, 0, 0)`,
                  }}
                >
                  {/* Render regular slides */}
                  {slides.map((slide, index) => (
                    <div
                      key={slide.sys.id}
                      ref={(el) => (slideRefs.current[index] = el)}
                      className={cx(
                        slideCommon,
                        Number(slides.length) === 2 && 'lg:w-[640px]',
                        'last:pr-5 last:lg:pr-9'
                      )}
                    >
                      <Quote className="h-full" {...slide.fields} />
                    </div>
                  ))}

                  {/* Add duplicate slides at the end for continuous marquee */}
                  {marquee &&
                    slides.map((slide, index) => (
                      <div
                        key={`${slide.sys.id}-duplicate`}
                        className={cx(
                          slideCommon,
                          Number(slides.length) === 2 && 'lg:w-[640px]',
                          'last:pr-5 last:lg:pr-9'
                        )}
                      >
                        <Quote className="h-full" {...slide.fields} />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8 h-full">
              {slides.map((slide) => (
                <Quote
                  key={slide.sys.id}
                  className={cx(slides.length === 2 ? 'lg:w-1/2' : 'lg:w-1/3')}
                  {...slide.fields}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </section>
  )
}
